import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  RestartAlt as RestartAltIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import PageContainer from './common/PageContainer';

interface UploadResponse {
  success: boolean;
  template?: {
    id: string;
    name: string;
    variables: Array<{
      name: string;
      type: string;
      label: string;
    }>;
  };
  error?: string;
}

const WordTemplateCreator: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const navigate = useNavigate();

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setUploadError('請選擇Word文檔文件 (.docx 或 .doc)');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setUploadError('文件大小不能超過10MB');
        return;
      }

      setSelectedFile(file);
      setUploadError(null);
      
      if (!templateName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setTemplateName(nameWithoutExt);
      }
    }
  }, [templateName]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      const mockEvent = {
        target: { files: [file] }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileSelect(mockEvent);
    }
  }, [handleFileSelect]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setUploadError('請選擇要上傳的Word文件');
      return;
    }

    if (!templateName.trim()) {
      setUploadError('請輸入模板名稱');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const formData = new FormData();
      formData.append('wordFile', selectedFile);
      formData.append('name', templateName.trim());
      formData.append('description', templateDescription.trim());

      const response = await fetch('/api/word-templates/upload', {
        method: 'POST',
        body: formData
      });

      const result: UploadResponse = await response.json();

      if (result.success && result.template) {
        setUploadSuccess(`模板 "${result.template.name}" 創建成功！檢測到 ${result.template.variables.length} 個變量。`);
        
        setSelectedFile(null);
        setTemplateName('');
        setTemplateDescription('');
        
        setTimeout(() => {
          navigate('/template-list');
        }, 3000);
        
      } else {
        setUploadError(result.error || '模板創建失敗');
      }

    } catch (error) {
      console.error('上傳失敗:', error);
      setUploadError('網絡錯誤，請重試');
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, templateName, templateDescription, navigate]);

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setTemplateName('');
    setTemplateDescription('');
    setUploadError(null);
    setUploadSuccess(null);
  }, []);

  return (
    <PageContainer maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          創建Word模板
        </Typography>
        <Typography variant="body1" color="text.secondary">
          上傳Word文檔文件來創建新的PDF模板
        </Typography>
      </Box>

      {/* 文件上傳區域 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. 選擇Word文件
        </Typography>
        
        <Box
          sx={{
            border: 2,
            borderStyle: 'dashed',
            borderColor: isDragging ? 'primary.main' : (selectedFile ? 'success.main' : 'divider'),
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: isDragging ? 'action.hover' : (selectedFile ? 'success.lighter' : 'background.paper'),
            cursor: 'pointer',
            transition: 'all 0.3s',
            position: 'relative',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload-input')?.click()}
        >
          {selectedFile ? (
            <Box>
              <DescriptionIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                大小: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ mt: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                }}
              >
                移除文件
              </Button>
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                拖拽Word文件到此處，或點擊選擇文件
              </Typography>
              <Typography variant="body2" color="text.secondary">
                支持 .docx 和 .doc 格式，最大10MB
              </Typography>
            </Box>
          )}
          
          <input
            id="file-upload-input"
            type="file"
            accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      </Paper>

      {/* 模板信息 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          2. 模板信息
        </Typography>
        
        <TextField
          fullWidth
          label="模板名稱"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="輸入模板名稱"
          required
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="模板描述"
          value={templateDescription}
          onChange={(e) => setTemplateDescription(e.target.value)}
          placeholder="描述這個模板的用途（可選）"
          multiline
          rows={3}
        />
      </Paper>

      {/* 使用說明 */}
      <Card sx={{ mb: 3, backgroundColor: 'info.lighter' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📝 如何創建Word模板
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="1. 在Microsoft Word中創建文檔" 
                secondary="設計你想要的佈局和樣式"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. 使用變量佔位符" 
                secondary={<code>{'在需要動態內容的地方使用：{{變量名}}'}</code>}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. 保存爲 .docx 格式" 
                secondary="確保文件格式正確"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. 上傳到此系統" 
                secondary="系統會自動識別所有變量"
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            🔧 變量示例
          </Typography>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'background.paper', 
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.8
          }}>
            <div>{'{{company_name}}'} - 公司名稱 (默認限制5字符)</div>
            <div>{'{{issue_date}}'} - 發出日期 (默認限制5字符)</div>
            <div>{'{{notice_title_3}}'} - 通知標題 (限制3字符)</div>
            <div>{'{{main_content}}'} - 主要內容 (默認限制5字符)</div>
            <div>{'{{contact_person}}'} - 聯繫人 (默認限制5字符)</div>
            <div>{'{{contact_phone}}'} - 聯繫電話 (默認限制5字符)</div>
            <div>{'{{department_name_10}}'} - 部門名稱 (限制10字符)</div>
            <div>{'{{document_number}}'} - 文檔編號 (默認限制5字符)</div>
            <div>{'{{recipient_name}}'} - 收件人姓名 (默認限制5字符)</div>
            <div>{'{{recipient_address_20}}'} - 收件人地址 (限制20字符)</div>
            <div>{'{{effective_date}}'} - 生效日期 (默認限制5字符)</div>
            <div>{'{{signature}}'} - 簽名 (默認限制5字符)</div>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>💡 字符限制規則：</strong><br/>
              • 變量名後加 <code>_數字</code> 可設置字符限制，例如 <code>{'{{notice_title_3}}'}</code> 限制3個字符<br/>
              • 沒有數字後綴的變量默認限制5個字符<br/>
              • 生成文檔時會自動驗證字符數量
            </Typography>
          </Alert>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            🖼️ 圖片變量示例
          </Typography>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'background.paper', 
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.8
          }}>
            <div>{'{{image_700x700}}'} - 圖片 (700x700像素)</div>
            <div>{'{{photo_300x200}}'} - 照片 (300x200像素)</div>
            <div>{'{{pic_500x500}}'} - 圖片 (500x500像素)</div>
            <div>{'{{img_1000x800}}'} - 圖片 (1000x800像素)</div>
          </Box>
          
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>🎨 圖片變量規則：</strong><br/>
              • 使用格式 <code>{'{{image_寬x高}}'}</code>、<code>{'{{photo_寬x高}}'}</code>、<code>{'{{pic_寬x高}}'}</code> 或 <code>{'{{img_寬x高}}'}</code><br/>
              • 系統會<strong>強制縮放</strong>上傳的圖片到指定尺寸（不保持比例）<br/>
              • 例如：1920x1080 的圖片會被壓縮成 700x700，完整顯示<br/>
              • 支持 JPG、PNG、GIF、WebP、BMP 等常見格式<br/>
              • 圖片會自動轉換為 PNG 格式以確保最佳兼容性
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* 錯誤和成功消息 */}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {uploadError}
        </Alert>
      )}

      {uploadSuccess && (
        <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
          <Typography variant="body1">{uploadSuccess}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            3秒後將跳轉到模板列表...
          </Typography>
        </Alert>
      )}

      {/* 操作按鈕 */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={resetForm}
          disabled={isUploading}
          size="large"
        >
          重置
        </Button>
        
        <Button
          variant="contained"
          startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          onClick={handleUpload}
          disabled={!selectedFile || !templateName.trim() || isUploading}
          size="large"
        >
          {isUploading ? '上傳中...' : '創建模板'}
        </Button>
      </Box>
    </PageContainer>
  );
};

export default WordTemplateCreator;
